import { useCallback, useEffect, useRef, useState } from "react";
import type { LocaleCode, PaymentCreateView, PaymentItem, PaymentStatus } from "@fiket/booth-schema";
import type { Route } from "../../store/router";
import { ApiEnvelopeError } from "../../lib/apiEnvelope";
import { createFailureRoute } from "./paymentFlow";
import { cancelPayment, createPayment, fetchPayment } from "./paymentApi";
import { trackError } from "../../telemetry/activity";
import {
  PAYMENT_POLL_INTERVAL_MS,
  PAYMENT_TIMER_MS,
  cancelOutcome,
  exitReasonFor,
  paymentOutcome,
  type PaymentExitReason,
} from "./paymentViewLogic";

export type PaymentUiState =
  | { kind: "creating" }
  | { kind: "pending"; view: PaymentCreateView; status: PaymentStatus | null; startedAtMs: number; lastOkAtMs: number | null; nowMs: number }
  /** items = 생성 응답 스냅숏 — 영상 항목 포함 여부를 store 에 남긴다 (BOOTH-80) */
  | { kind: "paid"; paymentId: string; items: PaymentItem[] }
  | { kind: "closed"; reason: PaymentExitReason }
  | { kind: "skip"; route: Route; code: string }
  | { kind: "error"; code: string };

const TICK_MS = 500;

/**
 * 결제 1건의 수명 — 생성 → 2초 폴링 → paid | closed, 3분 타이머(클라이언트 시계) → 취소.
 * `attempt` 가 바뀌면 처음부터(에러 화면 "다시 시도" = 새 paymentId, F3). 이탈 시 AbortController 로 fetch·sleep 을 함께 끊는다
 * (usePrintJob 과 같은 패턴). 폴링 실패는 상태를 바꾸지 않고 다음 주기에 재시도 — 배너 판정은 lastOkAtMs 로 화면이 한다 (F28).
 */
export function usePayment(input: {
  boothId: string | null;
  withVideo: boolean;
  language: LocaleCode;
  attempt: number;
  visitId: string | null;
}): { state: PaymentUiState; cancel: () => void; cancelling: boolean } {
  const [state, setState] = useState<PaymentUiState>({ kind: "creating" });
  const [cancelling, setCancelling] = useState(false);

  /** 실행 세대 — attempt/boothId 변경마다 증가. settle·closeWith 이 이전 세대 콜백을 걸러낸다 */
  const genRef = useRef(0);
  const controllerRef = useRef<AbortController | null>(null);
  const paymentIdRef = useRef<string | null>(null);
  /** 생성 응답 items — paid 전환 시(폴링·ALREADY_PAID) 함께 넘긴다 */
  const itemsRef = useRef<PaymentItem[]>([]);
  /** paid·closed·skip·error 뒤엔 어떤 이벤트도 상태를 바꾸지 않는다. 세대 전환 시 리셋 */
  const settledRef = useRef(false);
  /** 취소 요청 중복 방지 — 첫 이유 승리(손님 버튼 vs 타이머 경쟁). 세대 전환 시 리셋 */
  const cancellingRef = useRef(false);

  /** 타이머·틱을 ref 로 관리 — settle 에서 즉시 해제 가능, unmount 에서도 보강 */
  const timerRef = useRef<number | null>(null);
  const tickRef = useRef<number | null>(null);

  /**
   * paid·closed·skip·error 로 전환. gen 불일치(이전 세대) 또는 이미 종결이면 무시.
   * settle 즉시 timer·tick 을 정리해 이후 setInterval 콜백이 pending 상태를 건드리지 않는다.
   */
  const settle = useCallback((next: PaymentUiState, gen: number) => {
    if (gen !== genRef.current) return; // 이전 세대의 콜백
    if (settledRef.current) return;
    settledRef.current = true;
    if (timerRef.current !== null) { window.clearTimeout(timerRef.current); timerRef.current = null; }
    if (tickRef.current !== null) { window.clearInterval(tickRef.current); tickRef.current = null; }
    controllerRef.current?.abort();
    setState(next);
  }, []);

  /**
   * 취소(손님 버튼 또는 타이머). 폴링을 멈추고 서버에 cancel — 409 ALREADY_PAID 면 paid 로 (F29).
   * cancellingRef 로 중복 진입 차단(첫 이유 승리). cancelPayment 는 signal 없이 — 이탈 후에도 서버에 취소 전달.
   * gen 인자: 타이머는 세팅 시점 gen 을 고정해 전달, cancel() 은 호출 시점 genRef.current 를 읽는다.
   */
  const closeWith = useCallback(
    async (reason: PaymentExitReason, gen?: number) => {
      const myGen = gen ?? genRef.current; // cancel() 호출 시점 세대, 타이머는 세팅 시점 세대
      if (myGen !== genRef.current) return; // 이전 세대
      if (settledRef.current) return;
      if (cancellingRef.current) return; // 이미 취소 중 — 첫 이유 승리
      cancellingRef.current = true;
      setCancelling(true);

      const id = paymentIdRef.current;
      controllerRef.current?.abort(); // 폴링 중단 — settle 전이라 settledRef 는 아직 false

      if (!id) { settle({ kind: "closed", reason }, myGen); return; }

      let result: { ok: true } | { ok: false; code: string };
      try {
        await cancelPayment(id); // signal 없음 — 이탈 뒤에도 서버엔 취소를 전달한다
        result = { ok: true };
      } catch (err) {
        result = { ok: false, code: err instanceof ApiEnvelopeError ? err.code : "HTTP_ERROR" };
        if (result.code !== "ALREADY_PAID") {
          console.warn("[Payment] 취소 요청 실패 — 서버가 expiresAt 에 만료시킨다", err);
          trackError("payment_cancel_failed", err);
        }
      }

      // await 완료 후 세대가 바뀌었을 수 있다 — settle 내부에서 gen 재확인
      settle(cancelOutcome(result) === "paid" ? { kind: "paid", paymentId: id, items: itemsRef.current } : { kind: "closed", reason }, myGen);
    },
    [settle]
  );

  useEffect(() => {
    const gen = ++genRef.current; // 이 실행의 세대 번호 — 모든 settle·closeWith 호출에 전달
    settledRef.current = false;
    cancellingRef.current = false;
    paymentIdRef.current = null;
    itemsRef.current = [];
    setCancelling(false);
    setState({ kind: "creating" });

    const controller = new AbortController();
    controllerRef.current = controller;
    const { signal } = controller;

    if (!input.boothId) {
      settle({ kind: "error", code: "NO_BOOTH" }, gen);
      return;
    }
    const boothId = input.boothId;

    void (async () => {
      // 한 틱 양보 — dev StrictMode 는 effect 를 실행 → 즉시 정리 → 재실행하므로, 정리된 첫 실행이 서버에 결제를 만들지 않게
      // 여기서 abort 를 확인한다(2026-10-15 실측: 결제 레코드가 "결제 완료" + "대기" 두 건 생김). prod 엔 이중 실행이 없어 영향 없음.
      await new Promise((r) => setTimeout(r, 0));
      if (signal.aborted) return;
      let view: PaymentCreateView;
      try {
        view = await createPayment({ boothId, withVideo: input.withVideo, language: input.language, ...(input.visitId ? { visitId: input.visitId } : {}) }, signal);
      } catch (err) {
        if (signal.aborted) return;
        const code = err instanceof ApiEnvelopeError ? err.code : "HTTP_ERROR";
        console.warn("[Payment] 결제 생성 실패", err);
        trackError("payment_create_failed", err, { message: code });
        const route = createFailureRoute(code, input.withVideo);
        settle(route ? { kind: "skip", route, code } : { kind: "error", code }, gen);
        return;
      }
      if (signal.aborted) return;
      paymentIdRef.current = view.paymentId;
      itemsRef.current = view.items;
      const startedAtMs = Date.now();
      setState({ kind: "pending", view, status: null, startedAtMs, lastOkAtMs: null, nowMs: startedAtMs });

      // 3분 타이머 — iPad 와 서버 시계 차이를 피해 생성 성공 시각 기준 상대 시계 (F33)
      // gen 을 클로저로 고정 — 세대가 바뀐 뒤 타이머가 발화해도 closeWith 가 걸러낸다
      timerRef.current = window.setTimeout(() => void closeWith("expired", gen), PAYMENT_TIMER_MS);
      tickRef.current = window.setInterval(() => {
        setState((s) => (s.kind === "pending" ? { ...s, nowMs: Date.now() } : s));
      }, TICK_MS);

      while (!signal.aborted) {
        try {
          const status = await fetchPayment(view.paymentId, signal);
          if (signal.aborted) return;
          const outcome = paymentOutcome(status.status);
          if (outcome === "paid") { settle({ kind: "paid", paymentId: view.paymentId, items: view.items }, gen); return; }
          if (outcome === "closed") { settle({ kind: "closed", reason: exitReasonFor(status.status) }, gen); return; }
          const okAt = Date.now();
          setState((s) => (s.kind === "pending" ? { ...s, status: status.status, lastOkAtMs: okAt, nowMs: okAt } : s));
        } catch (err) {
          if (signal.aborted) return;
          // 레코드가 없으면 3분 동안 "연결 확인 중" 을 보일 이유가 없다
          if (err instanceof ApiEnvelopeError && (err.httpStatus === 404 || err.code === "PAYMENT_NOT_FOUND")) {
            settle({ kind: "closed", reason: "cancelled" }, gen);
            return;
          }
          console.warn("[Payment] 상태 조회 실패 — 다음 폴링에서 재시도", err);
          trackError("payment_poll_failed", err);
        }
        try { await sleep(PAYMENT_POLL_INTERVAL_MS, signal); } catch { return; }
      }
    })();

    return () => {
      controller.abort();
      // timer·tick 은 settle 에서 이미 정리되지만 미종결 상태로 unmount 될 때를 대비
      if (timerRef.current !== null) { window.clearTimeout(timerRef.current); timerRef.current = null; }
      if (tickRef.current !== null) { window.clearInterval(tickRef.current); tickRef.current = null; }
    };
    // attempt 가 바뀔 때만 처음부터. boothId/withVideo/language 는 화면 진입 시 고정된다
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [input.attempt, input.boothId]);

  const cancel = useCallback(() => void closeWith("cancelled"), [closeWith]);
  return { state, cancel, cancelling };
}

function sleep(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    const t = setTimeout(resolve, ms);
    signal.addEventListener("abort", () => { clearTimeout(t); reject(new DOMException("aborted", "AbortError")); }, { once: true });
  });
}
